/**
 * Mock topics + subtopics for the Recommendation Engine coach views.
 * Throwaway review scaffolding — the imported `TopicState` / `SubtopicState`
 * types are what actually constrain these shapes, so the compiler catches
 * drift here rather than a reader noticing it.
 * NOTHING here is wired to the database.
 */

import type { SubtopicState, TopicState } from '../types'

const RAW_MOCK_TOPICS: TopicState[] = [
  // 1 — Array
  {
    id: 'array',
    name: 'Array',
    chapter: 25,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 78,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'array__traversal_indexing',
        name: 'Traversal & Indexing',
        mastery: 88,
        stabilityDays: 45,
        difficulty: 3.0,
        retrievability: 0.93,
        effectiveMastery: 82,
        lastAttemptAt: '2026-05-10T10:00:00Z',
      },
      {
        id: 'array__in_place_mutation',
        name: 'In-place Mutation',
        mastery: 76,
        stabilityDays: 20,
        difficulty: 5.0,
        retrievability: 0.88,
        effectiveMastery: 67,
        lastAttemptAt: '2026-05-05T10:00:00Z',
      },
    ],
  },
  // 2 — Backtracking
  {
    id: 'backtracking',
    name: 'Backtracking',
    chapter: 39,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 44,
    prerequisiteIds: ['tree'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'backtracking__subsets_permutations',
        name: 'Subsets & Permutations',
        mastery: 58,
        stabilityDays: 9,
        difficulty: 6.5,
        retrievability: 0.68,
        effectiveMastery: 39,
        lastAttemptAt: '2026-04-30T14:00:00Z',
      },
      {
        id: 'backtracking__constraint_pruning',
        name: 'Constraint Pruning',
        mastery: 49,
        stabilityDays: 5,
        difficulty: 7.5,
        retrievability: 0.55,
        effectiveMastery: 27,
        lastAttemptAt: '2026-04-21T14:00:00Z',
      },
    ],
  },
  // 3 — Binary Search
  {
    id: 'binary_search',
    name: 'Binary Search',
    chapter: 29,
    minProficiencyThreshold: 65,
    maxProficiencyThreshold: 85,
    topicMastery: 41,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'binary_search__lower_upper_bound',
        name: 'Lower / Upper Bound',
        mastery: 70,
        stabilityDays: 14,
        difficulty: 5.5,
        retrievability: 0.78,
        effectiveMastery: 55,
        lastAttemptAt: '2026-04-28T11:00:00Z',
      },
      {
        id: 'binary_search__rotated_arrays',
        name: 'Rotated Arrays',
        mastery: 38,
        stabilityDays: 3,
        difficulty: 8.0,
        retrievability: 0.44,
        effectiveMastery: 17,
        lastAttemptAt: '2026-04-15T11:00:00Z',
      },
      {
        id: 'binary_search__search_on_answer',
        name: 'Search on Answer',
        mastery: 30,
        stabilityDays: 3,
        difficulty: 8.5,
        retrievability: 0.4,
        effectiveMastery: 12,
        lastAttemptAt: '2026-04-12T11:00:00Z',
      },
    ],
  },
  // 4 — Binary Tree
  {
    id: 'binary_tree',
    name: 'Binary Tree',
    chapter: 35,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 63,
    prerequisiteIds: ['recursion'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'binary_tree__traversals',
        name: 'Traversals',
        mastery: 82,
        stabilityDays: 30,
        difficulty: 4.5,
        retrievability: 0.91,
        effectiveMastery: 75,
        lastAttemptAt: '2026-05-08T09:00:00Z',
      },
      {
        id: 'binary_tree__path_problems',
        name: 'Path Problems',
        mastery: 55,
        stabilityDays: 8,
        difficulty: 7.0,
        retrievability: 0.62,
        effectiveMastery: 34,
        lastAttemptAt: '2026-04-25T09:00:00Z',
      },
    ],
  },
  // 5 — Bit Manipulation (not started, not in plan)
  {
    id: 'bit_manipulation',
    name: 'Bit Manipulation',
    chapter: null,
    minProficiencyThreshold: 50,
    maxProficiencyThreshold: 70,
    topicMastery: 8,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: false,
    subtopics: [
      {
        id: 'bit_manipulation__masking_shifting',
        name: 'Masking & Shifting',
        mastery: 10,
        stabilityDays: 1,
        difficulty: 5.0,
        retrievability: 0.1,
        effectiveMastery: 1,
        lastAttemptAt: null,
      },
      {
        id: 'bit_manipulation__xor_tricks',
        name: 'XOR Tricks',
        mastery: 7,
        stabilityDays: 1,
        difficulty: 6.0,
        retrievability: 0.1,
        effectiveMastery: 1,
        lastAttemptAt: null,
      },
    ],
  },
  // 6 — Depth-First Search
  {
    id: 'depth_first_search',
    name: 'Depth-First Search',
    chapter: 36,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 56,
    prerequisiteIds: ['recursion'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'depth_first_search__recursive_dfs',
        name: 'Recursive DFS',
        mastery: 74,
        stabilityDays: 18,
        difficulty: 5.0,
        retrievability: 0.84,
        effectiveMastery: 62,
        lastAttemptAt: '2026-05-03T15:00:00Z',
      },
      {
        id: 'depth_first_search__iterative_dfs',
        name: 'Iterative DFS',
        mastery: 52,
        stabilityDays: 7,
        difficulty: 6.5,
        retrievability: 0.6,
        effectiveMastery: 31,
        lastAttemptAt: '2026-04-22T15:00:00Z',
      },
    ],
  },
  // 7 — Dynamic Arrays
  {
    id: 'dynamic_arrays',
    name: 'Dynamic Arrays',
    chapter: 25,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 81,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'dynamic_arrays__amortized_append',
        name: 'Amortized Append',
        mastery: 88,
        stabilityDays: 60,
        difficulty: 3.5,
        retrievability: 0.97,
        effectiveMastery: 85,
        lastAttemptAt: '2026-05-09T14:00:00Z',
      },
      {
        id: 'dynamic_arrays__in_place_mutation',
        name: 'In-place Mutation',
        mastery: 79,
        stabilityDays: 28,
        difficulty: 4.5,
        retrievability: 0.91,
        effectiveMastery: 72,
        lastAttemptAt: '2026-05-06T10:00:00Z',
      },
    ],
  },
  // 8 — Dynamic Programming (gated: prerequisites not met)
  {
    id: 'dynamic_programming',
    name: 'Dynamic Programming',
    chapter: 40,
    minProficiencyThreshold: 50,
    maxProficiencyThreshold: 70,
    topicMastery: 12,
    prerequisiteIds: ['backtracking'],
    prerequisitesMet: false,
    includedInPlan: true,
    subtopics: [
      {
        id: 'dynamic_programming__1d_tabulation',
        name: '1D Tabulation',
        mastery: 18,
        stabilityDays: 3,
        difficulty: 8.5,
        retrievability: 0.1,
        effectiveMastery: 2,
        lastAttemptAt: null,
      },
      {
        id: 'dynamic_programming__2d_grid_dp',
        name: '2D Grid DP',
        mastery: 10,
        stabilityDays: 3,
        difficulty: 9.0,
        retrievability: 0.1,
        effectiveMastery: 1,
        lastAttemptAt: null,
      },
    ],
  },
  // 9 — Graph
  {
    id: 'graph',
    name: 'Graph',
    chapter: 36,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 37,
    prerequisiteIds: ['stack', 'recursion'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'graph__bfs_shortest_path',
        name: 'BFS Shortest Path',
        mastery: 50,
        stabilityDays: 8,
        difficulty: 6.5,
        retrievability: 0.63,
        effectiveMastery: 32,
        lastAttemptAt: '2026-04-24T16:00:00Z',
      },
      {
        id: 'graph__adjacency_modeling',
        name: 'Adjacency Modeling',
        mastery: 42,
        stabilityDays: 5,
        difficulty: 7.0,
        retrievability: 0.55,
        effectiveMastery: 23,
        lastAttemptAt: '2026-04-18T16:00:00Z',
      },
    ],
  },
  // 10 — Greedy (gated: prerequisites not met)
  {
    id: 'greedy',
    name: 'Greedy',
    chapter: 41,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 15,
    prerequisiteIds: ['backtracking'],
    prerequisitesMet: false,
    includedInPlan: true,
    subtopics: [
      {
        id: 'greedy__exchange_argument',
        name: 'Exchange Argument',
        mastery: 20,
        stabilityDays: 3,
        difficulty: 8.0,
        retrievability: 0.1,
        effectiveMastery: 2,
        lastAttemptAt: null,
      },
      {
        id: 'greedy__interval_scheduling',
        name: 'Interval Scheduling',
        mastery: 15,
        stabilityDays: 3,
        difficulty: 7.5,
        retrievability: 0.1,
        effectiveMastery: 2,
        lastAttemptAt: null,
      },
    ],
  },
  // 11 — Hash Table
  {
    id: 'hash_table',
    name: 'Hash Table',
    chapter: 30,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 72,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'hash_table__frequency_maps',
        name: 'Frequency Maps',
        mastery: 85,
        stabilityDays: 40,
        difficulty: 4.0,
        retrievability: 0.93,
        effectiveMastery: 79,
        lastAttemptAt: '2026-05-09T11:00:00Z',
      },
      {
        id: 'hash_table__constant_time_lookup',
        name: 'Constant-time Lookup',
        mastery: 76,
        stabilityDays: 22,
        difficulty: 4.5,
        retrievability: 0.87,
        effectiveMastery: 66,
        lastAttemptAt: '2026-05-04T11:00:00Z',
      },
    ],
  },
  // 12 — Heap (Priority Queue)
  {
    id: 'heap_priority_queue',
    name: 'Heap (Priority Queue)',
    chapter: 37,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 49,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'heap_priority_queue__top_k_selection',
        name: 'Top-K Selection',
        mastery: 65,
        stabilityDays: 12,
        difficulty: 5.5,
        retrievability: 0.74,
        effectiveMastery: 48,
        lastAttemptAt: '2026-04-29T13:00:00Z',
      },
      {
        id: 'heap_priority_queue__streaming_median',
        name: 'Streaming Median',
        mastery: 40,
        stabilityDays: 4,
        difficulty: 8.0,
        retrievability: 0.5,
        effectiveMastery: 20,
        lastAttemptAt: '2026-04-17T13:00:00Z',
      },
    ],
  },
  // 13 — Linked List
  {
    id: 'linked_list',
    name: 'Linked List',
    chapter: 34,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 66,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'linked_list__pointer_reversal',
        name: 'Pointer Reversal',
        mastery: 78,
        stabilityDays: 20,
        difficulty: 5.0,
        retrievability: 0.86,
        effectiveMastery: 67,
        lastAttemptAt: '2026-05-02T10:00:00Z',
      },
      {
        id: 'linked_list__fast_slow_pointers',
        name: 'Fast & Slow Pointers',
        mastery: 62,
        stabilityDays: 10,
        difficulty: 6.0,
        retrievability: 0.72,
        effectiveMastery: 45,
        lastAttemptAt: '2026-04-27T10:00:00Z',
      },
    ],
  },
  // 14 — Math (not started, not in plan)
  {
    id: 'math',
    name: 'Math',
    chapter: null,
    minProficiencyThreshold: 50,
    maxProficiencyThreshold: 70,
    topicMastery: 6,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: false,
    subtopics: [
      {
        id: 'math__modular_arithmetic',
        name: 'Modular Arithmetic',
        mastery: 8,
        stabilityDays: 1,
        difficulty: 5.5,
        retrievability: 0.1,
        effectiveMastery: 1,
        lastAttemptAt: null,
      },
      {
        id: 'math__combinatorics',
        name: 'Combinatorics',
        mastery: 6,
        stabilityDays: 1,
        difficulty: 6.5,
        retrievability: 0.1,
        effectiveMastery: 1,
        lastAttemptAt: null,
      },
    ],
  },
  // 15 — Matrix
  {
    id: 'matrix',
    name: 'Matrix',
    chapter: 28,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 60,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'matrix__grid_traversal',
        name: 'Grid Traversal',
        mastery: 74,
        stabilityDays: 16,
        difficulty: 4.5,
        retrievability: 0.82,
        effectiveMastery: 61,
        lastAttemptAt: '2026-05-01T12:00:00Z',
      },
      {
        id: 'matrix__in_place_rotation',
        name: 'In-place Rotation',
        mastery: 56,
        stabilityDays: 7,
        difficulty: 6.5,
        retrievability: 0.65,
        effectiveMastery: 36,
        lastAttemptAt: '2026-04-23T12:00:00Z',
      },
    ],
  },
  // 16 — Monotonic Stack
  {
    id: 'monotonic_stack',
    name: 'Monotonic Stack',
    chapter: 32,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 55,
    prerequisiteIds: ['stack'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'monotonic_stack__next_greater_element',
        name: 'Next Greater Element',
        mastery: 68,
        stabilityDays: 14,
        difficulty: 5.5,
        retrievability: 0.79,
        effectiveMastery: 54,
        lastAttemptAt: '2026-04-30T13:00:00Z',
      },
      {
        id: 'monotonic_stack__span_problems',
        name: 'Span Problems',
        mastery: 52,
        stabilityDays: 6,
        difficulty: 7.0,
        retrievability: 0.6,
        effectiveMastery: 31,
        lastAttemptAt: '2026-04-20T13:00:00Z',
      },
    ],
  },
  // 17 — Prefix Sum
  {
    id: 'prefix_sum',
    name: 'Prefix Sum',
    chapter: 43,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 58,
    prerequisiteIds: ['array'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'prefix_sum__1d_prefix_sums',
        name: '1D Prefix Sums',
        mastery: 72,
        stabilityDays: 16,
        difficulty: 4.0,
        retrievability: 0.83,
        effectiveMastery: 60,
        lastAttemptAt: '2026-05-01T11:00:00Z',
      },
      {
        id: 'prefix_sum__2d_prefix_sums',
        name: '2D Prefix Sums',
        mastery: 48,
        stabilityDays: 5,
        difficulty: 7.0,
        retrievability: 0.58,
        effectiveMastery: 28,
        lastAttemptAt: '2026-04-20T11:00:00Z',
      },
    ],
  },
  // 18 — Queue
  {
    id: 'queue',
    name: 'Queue',
    chapter: 32,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 73,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'queue__fifo_basics',
        name: 'FIFO Basics',
        mastery: 86,
        stabilityDays: 45,
        difficulty: 3.0,
        retrievability: 0.95,
        effectiveMastery: 82,
        lastAttemptAt: '2026-05-10T08:00:00Z',
      },
      {
        id: 'queue__deque_window',
        name: 'Deque Window',
        mastery: 65,
        stabilityDays: 11,
        difficulty: 6.0,
        retrievability: 0.73,
        effectiveMastery: 47,
        lastAttemptAt: '2026-04-28T08:00:00Z',
      },
    ],
  },
  // 19 — Recursion
  {
    id: 'recursion',
    name: 'Recursion',
    chapter: 33,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 76,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'recursion__base_case_design',
        name: 'Base Case Design',
        mastery: 88,
        stabilityDays: 50,
        difficulty: 4.0,
        retrievability: 0.95,
        effectiveMastery: 84,
        lastAttemptAt: '2026-05-09T09:00:00Z',
      },
      {
        id: 'recursion__recurrence_unrolling',
        name: 'Recurrence Unrolling',
        mastery: 70,
        stabilityDays: 15,
        difficulty: 6.0,
        retrievability: 0.8,
        effectiveMastery: 56,
        lastAttemptAt: '2026-04-30T09:00:00Z',
      },
    ],
  },
  // 20 — Sets and Maps
  {
    id: 'sets_and_maps',
    name: 'Sets and Maps',
    chapter: 30,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 70,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'sets_and_maps__set_operations',
        name: 'Set Operations',
        mastery: 80,
        stabilityDays: 28,
        difficulty: 4.0,
        retrievability: 0.9,
        effectiveMastery: 72,
        lastAttemptAt: '2026-05-06T12:00:00Z',
      },
      {
        id: 'sets_and_maps__map_aggregation',
        name: 'Map Aggregation',
        mastery: 72,
        stabilityDays: 18,
        difficulty: 5.0,
        retrievability: 0.85,
        effectiveMastery: 61,
        lastAttemptAt: '2026-05-02T12:00:00Z',
      },
    ],
  },
  // 21 — Sliding Window
  {
    id: 'sliding_window',
    name: 'Sliding Window',
    chapter: 38,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 52,
    prerequisiteIds: ['two_pointers'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'sliding_window__fixed_window',
        name: 'Fixed Window',
        mastery: 85,
        stabilityDays: 60,
        difficulty: 4.5,
        retrievability: 0.95,
        effectiveMastery: 81,
        lastAttemptAt: '2026-05-08T12:00:00Z',
      },
      {
        id: 'sliding_window__dynamic_window',
        name: 'Dynamic Window',
        mastery: 44,
        stabilityDays: 5,
        difficulty: 7.0,
        retrievability: 0.56,
        effectiveMastery: 25,
        lastAttemptAt: '2026-04-19T12:00:00Z',
      },
      {
        id: 'sliding_window__resetting_window',
        name: 'Resetting Window',
        mastery: 40,
        stabilityDays: 3,
        difficulty: 7.5,
        retrievability: 0.45,
        effectiveMastery: 18,
        lastAttemptAt: '2026-04-14T12:00:00Z',
      },
    ],
  },
  // 22 — Sorting
  {
    id: 'sorting',
    name: 'Sorting',
    chapter: 31,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 74,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'sorting__comparison_sorts',
        name: 'Comparison Sorts',
        mastery: 84,
        stabilityDays: 35,
        difficulty: 4.0,
        retrievability: 0.92,
        effectiveMastery: 77,
        lastAttemptAt: '2026-05-07T10:00:00Z',
      },
      {
        id: 'sorting__specialized_sorts',
        name: 'Specialized Sorts',
        mastery: 64,
        stabilityDays: 12,
        difficulty: 6.0,
        retrievability: 0.75,
        effectiveMastery: 48,
        lastAttemptAt: '2026-04-27T10:00:00Z',
      },
    ],
  },
  // 23 — Stack
  {
    id: 'stack',
    name: 'Stack',
    chapter: 32,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 78,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'stack__lifo_basics',
        name: 'LIFO Basics',
        mastery: 90,
        stabilityDays: 55,
        difficulty: 3.0,
        retrievability: 0.96,
        effectiveMastery: 86,
        lastAttemptAt: '2026-05-10T09:00:00Z',
      },
      {
        id: 'stack__expression_parsing',
        name: 'Expression Parsing',
        mastery: 68,
        stabilityDays: 14,
        difficulty: 5.5,
        retrievability: 0.78,
        effectiveMastery: 53,
        lastAttemptAt: '2026-04-29T09:00:00Z',
      },
    ],
  },
  // 24 — String
  {
    id: 'string',
    name: 'String',
    chapter: 26,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 68,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'string__character_scanning',
        name: 'Character Scanning',
        mastery: 82,
        stabilityDays: 32,
        difficulty: 3.5,
        retrievability: 0.9,
        effectiveMastery: 74,
        lastAttemptAt: '2026-05-06T11:00:00Z',
      },
      {
        id: 'string__pattern_matching',
        name: 'Pattern Matching',
        mastery: 58,
        stabilityDays: 8,
        difficulty: 7.0,
        retrievability: 0.65,
        effectiveMastery: 38,
        lastAttemptAt: '2026-04-24T11:00:00Z',
      },
    ],
  },
  // 25 — Topological Sort
  {
    id: 'topological_sort',
    name: 'Topological Sort',
    chapter: 42,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 38,
    prerequisiteIds: ['graph'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'topological_sort__kahns_algorithm',
        name: "Kahn's Algorithm",
        mastery: 48,
        stabilityDays: 6,
        difficulty: 7.0,
        retrievability: 0.6,
        effectiveMastery: 29,
        lastAttemptAt: '2026-04-21T14:00:00Z',
      },
      {
        id: 'topological_sort__dfs_ordering',
        name: 'DFS Ordering',
        mastery: 40,
        stabilityDays: 4,
        difficulty: 7.5,
        retrievability: 0.52,
        effectiveMastery: 21,
        lastAttemptAt: '2026-04-16T14:00:00Z',
      },
    ],
  },
  // 26 — Tree
  {
    id: 'tree',
    name: 'Tree',
    chapter: 35,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 59,
    prerequisiteIds: ['stack', 'recursion'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'tree__dfs_recursion',
        name: 'DFS Recursion',
        mastery: 76,
        stabilityDays: 21,
        difficulty: 5.0,
        retrievability: 0.86,
        effectiveMastery: 65,
        lastAttemptAt: '2026-05-03T15:00:00Z',
      },
      {
        id: 'tree__bfs_levels',
        name: 'BFS Levels',
        mastery: 64,
        stabilityDays: 14,
        difficulty: 5.5,
        retrievability: 0.8,
        effectiveMastery: 51,
        lastAttemptAt: '2026-04-29T15:00:00Z',
      },
      {
        id: 'tree__lowest_common_ancestor',
        name: 'Lowest Common Ancestor',
        mastery: 42,
        stabilityDays: 5,
        difficulty: 7.5,
        retrievability: 0.55,
        effectiveMastery: 23,
        lastAttemptAt: '2026-04-18T15:00:00Z',
      },
    ],
  },
  // 27 — Trie (not started, not in plan)
  {
    id: 'trie',
    name: 'Trie',
    chapter: 35,
    minProficiencyThreshold: 50,
    maxProficiencyThreshold: 70,
    topicMastery: 9,
    prerequisiteIds: ['tree'],
    prerequisitesMet: true,
    includedInPlan: false,
    subtopics: [
      {
        id: 'trie__prefix_insertion',
        name: 'Prefix Insertion',
        mastery: 12,
        stabilityDays: 1,
        difficulty: 6.0,
        retrievability: 0.1,
        effectiveMastery: 1,
        lastAttemptAt: null,
      },
      {
        id: 'trie__prefix_search',
        name: 'Prefix Search',
        mastery: 8,
        stabilityDays: 1,
        difficulty: 6.5,
        retrievability: 0.1,
        effectiveMastery: 1,
        lastAttemptAt: null,
      },
    ],
  },
  // 28 — Two Pointers
  {
    id: 'two_pointers',
    name: 'Two Pointers',
    chapter: 27,
    minProficiencyThreshold: 60,
    maxProficiencyThreshold: 80,
    topicMastery: 68,
    prerequisiteIds: [],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'two_pointers__converging_pointers',
        name: 'Converging Pointers',
        mastery: 82,
        stabilityDays: 30,
        difficulty: 4.0,
        retrievability: 0.9,
        effectiveMastery: 74,
        lastAttemptAt: '2026-05-04T09:00:00Z',
      },
      {
        id: 'two_pointers__fast_slow_pointers',
        name: 'Fast & Slow Pointers',
        mastery: 54,
        stabilityDays: 7,
        difficulty: 6.5,
        retrievability: 0.62,
        effectiveMastery: 33,
        lastAttemptAt: '2026-04-22T09:00:00Z',
      },
    ],
  },
  // 29 — Union Find
  {
    id: 'union_find',
    name: 'Union Find',
    chapter: 36,
    minProficiencyThreshold: 55,
    maxProficiencyThreshold: 75,
    topicMastery: 32,
    prerequisiteIds: ['graph'],
    prerequisitesMet: true,
    includedInPlan: true,
    subtopics: [
      {
        id: 'union_find__union_by_rank',
        name: 'Union by Rank',
        mastery: 42,
        stabilityDays: 5,
        difficulty: 7.0,
        retrievability: 0.57,
        effectiveMastery: 24,
        lastAttemptAt: '2026-04-19T16:00:00Z',
      },
      {
        id: 'union_find__path_compression',
        name: 'Path Compression',
        mastery: 36,
        stabilityDays: 4,
        difficulty: 7.5,
        retrievability: 0.5,
        effectiveMastery: 18,
        lastAttemptAt: '2026-04-14T16:00:00Z',
      },
    ],
  },
]

/** Fills per-subtopic ranges and defaults from parent topic. */
function normalizeMockTopics(topics: TopicState[]): TopicState[] {
  return topics.map((topic) => ({
    ...topic,
    subtopics: topic.subtopics.map((st) => {
      const base: SubtopicState = {
        ...st,
        isEnabled: st.isEnabled !== false,
        minProficiencyThreshold:
          st.minProficiencyThreshold ?? topic.minProficiencyThreshold,
        maxProficiencyThreshold:
          st.maxProficiencyThreshold ?? topic.maxProficiencyThreshold,
        rollupWeight: st.rollupWeight ?? 1,
      }
      if (topic.id === 'array' && st.id === 'array__in_place_mutation') {
        return {
          ...base,
          minProficiencyThreshold: 55,
          maxProficiencyThreshold: 75,
        }
      }
      return base
    }),
  }))
}

export const MOCK_TOPICS: TopicState[] = normalizeMockTopics(RAW_MOCK_TOPICS)
